import { createSelector } from 'reselect';
import { urls as locationsUrls } from 'Reducers/locations';
import * as urlHelper from 'Helpers/url';
import { paramsToQueryString } from 'Helpers/url';
import * as locationsHelper from 'Helpers/locations';
import { EMPTY_LOCATION, LOCATION_LEVEL, GRANTED } from 'Constants/locations';
import { isEmpty } from 'Helpers/objects';
import { getType } from 'Reducers/helpers/typeModifier';

export const selectedLocationSelector = createSelector(
    ({ locations }) => locations.selectedLocation,
    ({ locations }) => locations.gps,
    (selectedLocation, gps) => {
        return gps ? { ...selectedLocation, gps } : selectedLocation;
    }
);

export const olxAutosLocationIdSelector = createSelector(
    selectedLocationSelector,
    ({ type, id } = {}) => {
        return (type === 'CITY' && id) || 'unknown';
    }
);

export const gpsSelector = ({ locations: { gps }}) => gps;

export const isGpsEnabledInDeviceSelector = createSelector(
    gpsSelector,
    gps => gps && gps.status && gps.status === GRANTED || false
);

/**
 * @deprecated Use 'isSelectedLocationGPSSelector' instead
 */
export const isCurrentGPSLocationSelector = ({ locations: { gps }}) => gps && gps.currentLocation;

export const isFetchingGps = createSelector(
    gpsSelector,
    gps => gps && gps.isFetching
);

export const countryLocationSelector = createSelector(
    ({ locations }) => locations,
    ({ elements, startingLocation }) => {
        const initialLocation = elements[startingLocation];

        if (initialLocation) {
            return initialLocation;
        }
        return Object.values(elements).find(({ type }) => type === 'COUNTRY');
    }
);

/**
 * Used to determine whether the current locationn is gps
 */
export const isSelectedLocationGPSSelector = ({ locations: { gps, selectedLocation }}) => {
    const gpsLocation = gps && gps.location && gps.location[0];

    return gpsLocation && selectedLocation.id == gpsLocation.id;
};

export const selectedLocationNameSelector = createSelector(
    selectedLocationSelector,
    countryLocationSelector,
    isSelectedLocationGPSSelector,
    (_, props) => props && props.intl,
    (_, props) => props && props.marketConfig,
    (selectedLocation, country, isSelectedLocationGPS, intl, marketConfig) => {
        const cookieLocations = locationsHelper.readLocationPathCookie();
        const locationFromCookie = cookieLocations && !!cookieLocations.length ? cookieLocations[0] : {};

        const hideCurrentLocationName = marketConfig && marketConfig.get('hideCurrentLocationName');

        if (isSelectedLocationGPS && hideCurrentLocationName) {
            return intl.formatMessage({ id: 'current_gps_location_label' });
        }

        return (!hideCurrentLocationName && locationsHelper.getLocationName(selectedLocation && selectedLocation.addressComponents, LOCATION_LEVEL))
          || (selectedLocation && selectedLocation.name)
          || (locationFromCookie.gps && intl.formatMessage({ id: 'current_gps_location_label' }))
          || (country && country.name)
          || '';
    }
);

const normalizeLocationItem = locationItem => {
    if (locationItem && locationItem.children) {
        const { children, ...rest } = locationItem; // eslint-disable-line no-unused-vars

        return rest;
    }
    return locationItem;
};

export const locationByIdSelector = ({ locations: { elements }}) => locationId => (
    locationId && elements && normalizeLocationItem(elements[locationId])
);

export const locationByLatLonSelector = ({ locations: { elements }}) => (lat, lon) => {
    if (elements && lat && lon) {
        return normalizeLocationItem(
            Object.values(elements).find(
                ({ latitude, longitude }) => latitude === lat && longitude === lon)
        );
    }
    return undefined;
};

export const isFetchingLocationByLatLonFromCollection = ({ locations: { isFetching }}) => params =>
    isFetching[getType('LOCATIONS', `${locationsUrls.getLocations}?${urlHelper.paramsToQueryString(params)}`)];

export const getLocationByLatLongFromCollection = ({ locations: { collections }}, params) =>
    collections[getType('LOCATIONS', `${locationsUrls.getLocations}?${urlHelper.paramsToQueryString(params)}`)];

export const getLocationElement = (state, params) =>
    state.locations.elements[params.locationId] || EMPTY_LOCATION;

export const getLocationElements = state => state.locations.elements;

export const isFetchingLocationPathSelector = ({ locations: { isFetching }}) => params =>
    isFetching[getType('LOCATIONS', `${locationsUrls.getLocationPath}?${urlHelper.paramsToQueryString(params)}`)];

export const isFetchingLocationByIdSelector = ({ locations: { isFetching }}) => id =>
    isFetching[getType('LOCATIONS', locationsUrls.getLocationPathById(id))];

export const isFetchingLocationSelector = ({ locations: { isFetching }}, { id, lang }) =>
    isFetching[getType('LOCATIONS', locationsUrls.getLocation(id) + lang ? `?lang=${ lang}` : '')];

export const getCountryId = state => state.locations.countryId;

export const suggestionsSelector = createSelector(
    ({ locations }) => locations,
    state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang,
    (_, params) => params,
    (locations, lang, params) => {
        if (lang) {
            params.lang = lang;
        }

        const collection = locations.collections[getType('AUTOCOMPLETE_LOCATIONS', `${locationsUrls.getAutocomplete}?${paramsToQueryString(params)}`)];

        return collection && collection.suggestions;
    }
);

export const isFetchingLocationByParentId = ({ locations: { isFetching }}) => id =>
    isFetching[`${locationsUrls.getLocations}?${urlHelper.paramsToQueryString({ parent: id })}`];

export const getLocationsFromGPSSelector = createSelector(
    ({ locations: { collections }}) => collections,
    getLocationElements,
    ({ locations: { gps }}) => gps,
    (collections, elements, gps) => {
        if (gps && gps.latitude && gps.longitude) {
            const url = `${locationsUrls.getLocationPath}?${urlHelper.paramsToQueryString({ lat: gps.latitude, lon: gps.longitude })}`;
            const type = getType('LOCATIONS', url);
            const selectedCollection = collections[type];

            if (!isEmpty(selectedCollection) && !isEmpty(elements)) {
                return elements[selectedCollection[0]];
            }
            return null;
        }
        return null;
    }
);

